import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/context/AppContext';
import { useMerchantId } from '@/hooks/useMerchantId';
import { useCashierBranchId } from '@/hooks/api/branches';
import { apiClient } from '@/lib/api-client';
import { formatUZS } from '@/lib/format';
import { enqueueSale } from '@/lib/offline-queue';
import POSLayout from '@/components/layout/POSLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, Loader2, UserPlus, ChevronDown, ChevronUp, Search, UserCheck, Split } from 'lucide-react';
import { PaymentMethod } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { apiErrorMessage } from '@/lib/api-error';

const PHONE_REGEX = /^\+998\d{9}$/;

function formatPhone(val: string): string {
  const digits = val.replace(/\D/g, '');
  if (!digits) return '+998';
  const full = digits.startsWith('998') ? digits : '998' + digits;
  return '+' + full.slice(0, 12);
}

export default function Checkout() {
  const { t } = useTranslation();
  const { cart, cartTotal, clearCart } = useApp();
  const { merchantId } = useMerchantId();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const paymentMethod = (location.state as any)?.paymentMethod as PaymentMethod || 'cash';

  const [showCustomer, setShowCustomer] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('+998');
  const [phoneError, setPhoneError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState<{ id: number; full_name: string; phone: string } | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Aralash to'lov
  const [splitMode, setSplitMode] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState<Record<string, string>>({ cash: '', card: '', hisob_wallet: '' });

  const splitPayments = Object.entries(splitAmounts)
    .map(([method, val]) => ({ method, amount: Math.round(Number(val) || 0) }))
    .filter((p) => p.amount > 0);
  const splitTotal = splitPayments.reduce((s, p) => s + p.amount, 0);
  const splitRemaining = cartTotal - splitTotal;
  const splitValid = !splitMode || (splitPayments.length > 0 && Math.abs(splitRemaining) <= 1);


  // Debounced phone lookup
  const lookupCustomerByPhone = useCallback(async (phone: string) => {
    if (!merchantId || !PHONE_REGEX.test(phone)) {
      setFoundCustomer(null);
      return;
    }
    setIsSearching(true);
    try {
      const { data } = await apiClient.GET('/customers', { params: { query: { search: phone } } });
      const match = data?.data?.find(c => c.phone === phone);
      if (match) {
        setFoundCustomer({ id: match.id, full_name: match.full_name, phone: match.phone });
        setCustomerName(match.full_name);
      } else {
        setFoundCustomer(null);
      }
    } catch {
      setFoundCustomer(null);
    } finally {
      setIsSearching(false);
    }
  }, [merchantId]);

  const branchId = useCashierBranchId();

  const createSale = useMutation({
    mutationFn: async () => {
      if (!merchantId || !branchId) throw new Error(t('pos_checkout.merchant_missing'));

      if (showCustomer && customerName.trim() && customerPhone.trim() && !PHONE_REGEX.test(customerPhone)) {
        throw new Error(t('pos_checkout.phone_invalid'));
      }
      const customerInfo =
        showCustomer && customerName.trim() && PHONE_REGEX.test(customerPhone)
          ? { full_name: customerName.trim(), phone: customerPhone.trim() }
          : null;

      const items = cart.map(item => ({
        product_id: Number(item.product.id),
        quantity: item.quantity,
        unit: item.selected_unit || item.product.unit || undefined,
      }));

      // Oflayn — sotuv navbatga olinadi, internet qaytganda avtomatik yuboriladi.
      // Mijoz bog'lash flushSalesQueue'da hal qilinadi (tarmoq bo'lmagani uchun
      // bu yerda customer_id ni oldindan aniqlab bo'lmaydi).
      if (!navigator.onLine) {
        try {
          await enqueueSale({
            body: {
              branch_id: branchId,
              items,
              payment_method: paymentMethod,
              payments: splitMode ? splitPayments : undefined,
              customer_id: foundCustomer?.id ?? null,
            },
            customerInfo: foundCustomer ? null : customerInfo,
          });
        } catch (e) { console.error('enqueue error', e); }
        return { queued: true };
      }

      // If customer info provided and not already resolved by the phone
      // lookup, create the customer first.
      let customerId: number | null = foundCustomer?.id ?? null;
      if (!customerId && customerInfo) {
        const { data: custData, error: custError } = await apiClient.POST('/customers', {
          body: { full_name: customerInfo.full_name, phone: customerInfo.phone, source: 'pos_sale' } as never,
        });
        if (custError) {
          throw new Error(apiErrorMessage(custError, t('pos_checkout.customer_create_error')));
        }
        customerId = (custData as { data: { id: number } }).data.id;
      }

      const { data, error } = await apiClient.POST('/sales', {
        body: {
          branch_id: branchId,
          items,
          payment_method: paymentMethod,
          payments: splitMode ? splitPayments : undefined,
          customer_id: customerId,
        } as never,
      });
      if (error) {
        throw new Error(apiErrorMessage(error, t('pos_checkout.sale_create_error')));
      }
      return { ...data, customerId };
    },
    onSuccess: (data) => {
      // Oflayn navbatga olingan sotuv — chekni "navbatda" belgisi bilan ko'rsatamiz
      if ((data as any)?.queued) {
        const receiptItems = cart.map(item => ({
          name: item.product.name,
          quantity: item.quantity,
          total: item.total,
          mhik: item.product.mhik || '',
          unit: item.selected_unit || item.product.unit || t('pos_common.unit_default'),
        }));
        clearCart();
        toast({
          title: t('pos_checkout.queued_title'),
          description: t('pos_checkout.queued_desc'),
        });
        navigate('/pos/receipt', {
          state: {
            queued: true,
            total: cartTotal,
            method: paymentMethod,
            items: receiptItems.length,
            cartItems: receiptItems,
            customerName: showCustomer && customerName.trim() ? customerName.trim() : null,
          },
        });
        return;
      }
      // Customer totals (total_purchases/total_spent) are computed by the
      // backend from real sale records — no client-side increment call.
      queryClient.invalidateQueries({ queryKey: ['/sales'] });
      queryClient.invalidateQueries({ queryKey: ['/stocks'] });
      queryClient.invalidateQueries({ queryKey: ['/customers'] });
      const receiptItems = cart.map(item => ({
        name: item.product.name,
        quantity: item.quantity,
        total: item.total,
        mhik: item.product.mhik || '',
        unit: item.selected_unit || item.product.unit || t('pos_common.unit_default'),
      }));
      clearCart();
      navigate('/pos/receipt', {
        state: {
          total: cartTotal,
          method: paymentMethod,
          items: receiptItems.length,
          saleId: (data as { data?: { id?: number } })?.data?.id,
          cartItems: receiptItems,
          customerName: showCustomer && customerName.trim() ? customerName.trim() : null,
        },
      });
    },
    onError: (err: Error) => {
      toast({ title: t('pos_checkout.error'), description: err.message, variant: 'destructive' });
    },
  });

  const handlePhoneChange = (val: string) => {
    const formatted = formatPhone(val);
    setCustomerPhone(formatted);
    setPhoneError('');
    setFoundCustomer(null);
    // Debounce lookup
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      lookupCustomerByPhone(formatted);
    }, 500);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const isCustomerValid = !showCustomer || (
    (!customerName.trim() && customerPhone === '+998') || // both empty = skip
    (customerName.trim() && PHONE_REGEX.test(customerPhone)) // both filled = valid
  );

  const customerPartiallyFilled = showCustomer && (
    (customerName.trim() && !PHONE_REGEX.test(customerPhone)) ||
    (!customerName.trim() && customerPhone !== '+998')
  );

  const paymentLabel = paymentMethod === 'hisob_wallet' ? t('pos_pay.wallet') : paymentMethod === 'cash' ? t('pos_pay.cash') : paymentMethod === 'card' ? t('pos_pay.card') : t('pos_pay.murabaha');

  return (
    <POSLayout>
      <div className="max-w-lg mx-auto p-6 animate-fade-in">
        <h2 className="text-lg font-semibold text-foreground mb-4">{t('pos_checkout.title')}</h2>

        <div className="bg-card border border-border rounded-xl p-4 mb-4 space-y-2">
          {cart.map(item => (
            <div key={item.product.id} className="flex justify-between text-sm">
              <span className="text-foreground">{item.product.name} × {item.quantity}</span>
              <span className="text-foreground font-medium">{formatUZS(item.total)}</span>
            </div>
          ))}
          <div className="border-t border-border pt-2 mt-2 flex justify-between">
            <span className="font-semibold text-foreground">{t('pos_cart.total')}</span>
            <span className="font-semibold text-foreground">{formatUZS(cartTotal)}</span>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 mb-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('pos_cart.payment_method')}</p>
              <p className="text-sm font-medium text-foreground">{splitMode ? t('pos_checkout.split') : paymentLabel}</p>
            </div>
            <Button
              type="button"
              variant={splitMode ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setSplitMode(!splitMode)}
            >
              <Split size={14} className="mr-1.5" />
              {splitMode ? t('pos_checkout.single') : t('pos_checkout.split')}
            </Button>
          </div>

          {splitMode && (
            <div className="space-y-2 border-t border-border pt-3">
              {[
                { key: 'cash', label: t('pos_pay.cash') },
                { key: 'card', label: t('pos_pay.card') },
                { key: 'hisob_wallet', label: t('pos_pay.wallet') },
              ].map((m) => (
                <div key={m.key} className="flex items-center gap-3">
                  <span className="text-sm text-foreground w-28">{m.label}</span>
                  <Input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    placeholder="0"
                    value={splitAmounts[m.key]}
                    onChange={(e) => setSplitAmounts((prev) => ({ ...prev, [m.key]: e.target.value }))}
                    className="text-sm"
                  />
                  {splitRemaining > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-xs"
                      onClick={() =>
                        setSplitAmounts((prev) => ({
                          ...prev,
                          [m.key]: String(Math.round(Number(prev[m.key] || 0) + splitRemaining)),
                        }))
                      }
                    >
                      {t('pos_checkout.remainder')}
                    </Button>
                  )}
                </div>
              ))}
              <div className="flex justify-between text-sm pt-1">
                <span className="text-muted-foreground">{t('pos_checkout.entered')}</span>
                <span className="font-medium text-foreground">{formatUZS(splitTotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('pos_checkout.remainder')}</span>
                <span className={`font-medium ${Math.abs(splitRemaining) <= 1 ? 'text-green-600' : 'text-destructive'}`}>
                  {formatUZS(splitRemaining)}
                </span>
              </div>
            </div>
          )}
        </div>


        {/* Optional Customer Section */}
        <div className="bg-card border border-border rounded-xl mb-6 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowCustomer(!showCustomer)}
            className="w-full flex items-center justify-between p-4 text-sm text-muted-foreground hover:bg-accent/50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <UserPlus size={16} />
              {t('pos_checkout.attach_customer')}
            </span>
            {showCustomer ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showCustomer && (
            <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">{t('pos_checkout.phone')}</label>
                <div className="relative">
                  <Input
                    placeholder="+998 90 123 45 67"
                    value={customerPhone}
                    onChange={e => handlePhoneChange(e.target.value)}
                    className="text-sm font-mono pr-8"
                    maxLength={13}
                  />
                  {isSearching && (
                    <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
                  )}
                  {!isSearching && foundCustomer && (
                    <UserCheck size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-green-500" />
                  )}
                </div>
                {foundCustomer && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <UserCheck size={12} />
                    {t('pos_checkout.customer_found')}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">{t('pos_checkout.full_name')}</label>
                <Input
                  placeholder={t('pos_checkout.full_name_placeholder')}
                  value={customerName}
                  onChange={e => { setCustomerName(e.target.value); if (foundCustomer) setFoundCustomer(null); }}
                  className="text-sm"
                />
              </div>
              {customerPartiallyFilled && (
                <p className="text-xs text-destructive">
                  {!customerName.trim()
                    ? t('pos_checkout.name_required')
                    : t('pos_checkout.phone_format')}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('pos_checkout.phone_hint')}
              </p>
            </div>
          )}
        </div>

        <Button
          onClick={() => createSale.mutate()}
          disabled={cart.length === 0 || createSale.isPending || !merchantId || !branchId || !isCustomerValid || customerPartiallyFilled || !splitValid}
          className="w-full h-12 text-base"
        >
          {createSale.isPending ? (
            <><Loader2 size={18} className="mr-2 animate-spin" /> {t('pos_checkout.saving')}</>
          ) : (
            <><CheckCircle size={18} className="mr-2" /> {t('pos_checkout.finish')}</>
          )}
        </Button>
      </div>
    </POSLayout>
  );
}